import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, Home, HelpCircle, Trophy, Play, Calendar, X, BookOpen, Video, VideoOff } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { useGameMode } from "@/contexts/GameModeContext";
import { toast } from "sonner";
import { clearCachedOverlayState } from "@/components/game/managers/OverlayManager";
import { useViewMode } from "@/contexts/ViewModeContext";

interface HamburgerMenuProps {
  triggerClassName?: string;
}

export const HamburgerMenu = ({ triggerClassName = "" }: HamburgerMenuProps) => {
  const { currentMode, availableModes, switchToMode } = useGameMode();
  const { isLivestream, setViewMode } = useViewMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  
  // Check if we're on an informational page (not in active gameplay)
  const isInformationalPage = ['/how-to-play', '/babbleranks'].includes(location.pathname);
  
  console.log('HamburgerMenu Debug:', {
    currentPath: location.pathname,
    isInformationalPage,
    currentMode,
    timestamp: new Date().toISOString()
  });

  const handleModeSwitch = async (mode: string) => {
    // Don't switch if already in the same mode AND on the game page
    if (mode === currentMode && location.pathname === "/game") return;
    if (isSwitching) return;

    trackEvent('mode_switch', { from_mode: currentMode, to_mode: mode, source: 'menu' });
    setIsSwitching(true);
    try {
      // Set a guard to prevent persistence during mode switching (Android fix)
      try {
        localStorage.setItem('babblegram_mode_switch_in_progress', '1');
        localStorage.setItem('babblegram_mode_switch_target_mode', mode);
      } catch {}

      clearCachedOverlayState();
      // Always preserve state to maintain mode independence
      await switchToMode(mode as any, true);
      
      // Wait a brief moment to ensure storage operations complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force component re-mount for proper mode switch when already on game page
      if (location.pathname === "/game") {
        // Use navigate instead of reload to preserve streak data in cookies
        navigate("/game", { replace: true });
        // Force re-render by adding a timestamp to trigger useEffect
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('mode-switched', { detail: { mode } }));
          window.dispatchEvent(new CustomEvent('babblegram_mode_switched', { detail: { mode } }));
        }, 50);
      } else {
        navigate("/game");
      }
    } catch (error) {
      console.error("Failed to switch mode:", error);
      toast.error("Failed to switch game mode");
    } finally {
      setIsSwitching(false);
      setIsOpen(false);
    }
  };

  const handleNavigation = (path: string) => {
    trackEvent('menu_navigate', { destination: path });
    navigate(path);
    setIsOpen(false);
  };

  const handleLivestreamToggle = () => {
    const nextMode = isLivestream ? "standard" : "livestream";
    setViewMode(nextMode, "menu");
    setIsOpen(false);
    if (location.pathname !== "/game") navigate("/game");
  };

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'daily':
        return <Calendar className="h-4 w-4" />;
      case 'practice':
        return <Play className="h-4 w-4" />;
      default:
        return <Play className="h-4 w-4" />;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`h-9 w-9 ${triggerClassName}`}
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5 text-gray-400 hover:text-white" />
        </Button>
      </SheetTrigger>
      
      <SheetContent side="right" className="w-full sm:w-80 bg-slate-900 border-slate-800 p-0 z-[10001]">
        <div className="flex flex-col h-full">
          <SheetHeader className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-white text-xl font-bold">MENU</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 text-gray-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Game Modes Section */}
            <div>
              <h3 className="text-gray-400 text-sm font-medium mb-4 uppercase tracking-wider">Game Modes</h3>
              <div className="space-y-2">
                {Object.entries(availableModes)
                  .filter(([mode]) => mode !== 'custom')
                  .map(([mode, config]) => {
                    // Only disable current mode when actively on game page
                    const isCurrentModeDisabled = location.pathname === "/game" && mode === currentMode;
                    
                    return (
                      <button
                        key={mode}
                        onClick={() => handleModeSwitch(mode)}
                        disabled={!config.isEnabled || isSwitching || isCurrentModeDisabled}
                        className="w-full flex items-center gap-4 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                      >
                        <div className="text-gray-400 group-hover:text-white">
                          {getModeIcon(mode)}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-white font-medium">
                            {config.name} {!isInformationalPage && mode === currentMode && "(Current)"}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {config.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </div>

            {/* Navigation Section */}
            <div>
              <h3 className="text-gray-400 text-sm font-medium mb-4 uppercase tracking-wider">Display</h3>
              <Button
                type="button"
                variant="ghost"
                onClick={handleLivestreamToggle}
                className="w-full h-auto flex items-center justify-start gap-4 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
              >
                {isLivestream
                  ? <VideoOff className="h-5 w-5 text-gray-400 group-hover:text-white" />
                  : <Video className="h-5 w-5 text-gray-400 group-hover:text-white" />}
                <div className="flex-1 text-left">
                  <div className="text-white font-medium">
                    {isLivestream ? "Exit Livestream mode" : "Livestream mode"}
                  </div>
                  <div className="text-gray-400 text-sm whitespace-normal">
                    {isLivestream ? "Return to the standard layout" : "Camera and vertical recording layout"}
                  </div>
                </div>
              </Button>
            </div>

            {/* Navigation Section */}
            <div>
              <h3 className="text-gray-400 text-sm font-medium mb-4 uppercase tracking-wider">Navigation</h3>
              <div className="space-y-2">
                <button
                  onClick={() => handleNavigation("/")}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                >
                  <Home className="h-5 w-5 text-gray-400 group-hover:text-white" />
                  <span className="text-white font-medium">Home</span>
                </button>
                
                <button
                  onClick={() => handleNavigation("/how-to-play")}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                >
                  <HelpCircle className="h-5 w-5 text-gray-400 group-hover:text-white" />
                  <span className="text-white font-medium">How to Play</span>
                </button>
                
                <button
                  onClick={() => handleNavigation("/babbleranks")}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                >
                  <Trophy className="h-5 w-5 text-gray-400 group-hover:text-white" />
                  <span className="text-white font-medium">Babble Ranks</span>
                </button>

                <button
                  onClick={() => handleNavigation("/blog")}
                  className="w-full flex items-center gap-4 p-4 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors group"
                >
                  <BookOpen className="h-5 w-5 text-gray-400 group-hover:text-white" />
                  <span className="text-white font-medium">BabbleBlog</span>
                </button>
              </div>
            </div>

            {/* Social Links Section */}
            <div className="mt-auto">
              <h3 className="text-gray-400 text-sm font-medium mb-4 uppercase tracking-wider">Follow Us</h3>
              <div className="flex justify-center gap-6">
                <a 
                  href="https://x.com/babblegram_game" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <span className="text-xl font-bold">𝕏</span>
                </a>
                <a 
                  href="https://www.instagram.com/babblegram_game" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                >
                  Instagram
                </a>
                <a 
                  href="https://www.tiktok.com/@babblegram_game" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                >
                  TikTok
                </a>
              </div>
            </div>

            <div className="text-gray-500 text-xs text-center pt-4">
              © {new Date().getFullYear()} BabbleGram. Created by{" "}
              <a
                href="https://www.darawalsh.me"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white underline"
              >
                Dara Walsh
              </a>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};